<template>
  <div :class="style.list">
    <DragDropItemBase
      v-for="(item, index) in localItems"
      :key="getItemKey(item, index)"
      :item-id="index"
      :name="getItemName(item)"
      :value="getItemValue(item)"
      :name-placeholder="namePlaceholder"
      :value-placeholder="valuePlaceholder"
      :suffix="suffix"
      :disabled="disabled"
      :readonly="readonly"
      :error="error"
      :value-input-mode="valueInputMode"
      @update:name="updateItemName(index, $event)"
      @update:value="updateItemValue(index, $event)"
      @change:name="handleItemNameChange(index, $event)"
      @change:value="handleItemValueChange(index, $event)"
      @drop-item="handleDrop"
    >
      <template v-if="$slots.actions" #actions>
        <slot
          name="actions"
          :item="item"
          :index="index"
          :remove="() => removeItem(index)"
        />
      </template>
    </DragDropItemBase>
  </div>
</template>

<script lang="ts">
import {defineComponent, PropType} from "vue";
import DragDropItemBase from "@/elements/UI/list/drag/Item/DragDropItemBase.vue";
import style from "../Item/DragDropItem.module.scss";

type ListItem = Record<string, unknown>;

export default defineComponent({
  name: "DragDropListBase",
  components: {
    DragDropItemBase
  },
  emits: [
    "update:modelValue",
    "change",
    "remove",
    "update:item",
    "change:item"
  ],
  props: {
    modelValue: {
      type: Array as PropType<ListItem[]>,
      default: () => []
    },
    itemKeyField: {
      type: String,
      default: ""
    },
    nameField: {
      type: String,
      default: ""
    },
    valueField: {
      type: String,
      default: "value"
    },
    itemLabelField: {
      type: String,
      default: ""
    },
    namePlaceholder: {
      type: String,
      default: "Текст"
    },
    valuePlaceholder: {
      type: String,
      default: ""
    },
    suffix: {
      type: String,
      default: "₽"
    },
    disabled: {
      type: Boolean,
      default: false
    },
    readonly: {
      type: Boolean,
      default: false
    },
    error: {
      type: Boolean,
      default: false
    },
    removable: {
      type: Boolean,
      default: true
    },
    valueInputMode: {
      type: String,
      default: "text"
    }
  },
  data() {
    return {
      style,
      localItems: [...this.modelValue] as ListItem[]
    };
  },
  computed: {
    resolvedNameField(): string {
      return this.nameField || this.itemLabelField || "name";
    },

    resolvedValueField(): string {
      return this.valueField || "value";
    }
  },
  watch: {
    modelValue: {
      deep: true,
      handler(newValue: ListItem[]) {
        this.localItems = [...newValue];
      }
    }
  },
  methods: {
    getItemKey(item: ListItem, index: number): string | number {
      if (this.itemKeyField) {
        const key = item[this.itemKeyField];

        if (typeof key === "string" || typeof key === "number") {
          return key;
        }
      }

      return index;
    },

    getFieldValue(item: ListItem, field: string): string {
      const value = item[field];

      if (typeof value === "string" || typeof value === "number") {
        return String(value);
      }

      return "";
    },

    getItemName(item: ListItem): string {
      return this.getFieldValue(item, this.resolvedNameField);
    },

    getItemValue(item: ListItem): string {
      return this.getFieldValue(item, this.resolvedValueField);
    },

    updateItemName(index: number, value: string): void {
      this.updateItemField(index, this.resolvedNameField, value);
    },

    updateItemValue(index: number, value: string): void {
      this.updateItemField(index, this.resolvedValueField, value);
    },

    updateItemField(index: number, field: string, value: string): void {
      if (index < 0 || index >= this.localItems.length) {
        return;
      }

      const updatedItem: ListItem = {
        ...this.localItems[index],
        [field]: value
      };

      const updatedItems = [...this.localItems];
      updatedItems.splice(index, 1, updatedItem);

      this.localItems = updatedItems;

      this.$emit("update:modelValue", updatedItems);
      this.$emit("change", updatedItems);
      this.$emit("update:item", updatedItem, index, field, value);
    },

    handleItemNameChange(index: number, value: string): void {
      this.handleItemChange(index, this.resolvedNameField, value);
    },

    handleItemValueChange(index: number, value: string): void {
      this.handleItemChange(index, this.resolvedValueField, value);
    },

    handleItemChange(index: number, field: string, value: string): void {
      const item = this.localItems[index];

      if (!item) {
        return;
      }

      this.$emit("change:item", item, index, field, value);
    },

    handleDrop(payload: {
      sourceId: string | number;
      targetId: string | number;
    }): void {
      const fromIndex = Number(payload.sourceId);
      const toIndex = Number(payload.targetId);

      if (
        Number.isNaN(fromIndex) ||
        Number.isNaN(toIndex) ||
        fromIndex === toIndex ||
        fromIndex < 0 ||
        toIndex < 0 ||
        fromIndex >= this.localItems.length ||
        toIndex >= this.localItems.length
      ) {
        return;
      }

      const updatedItems = [...this.localItems];
      const [movedItem] = updatedItems.splice(fromIndex, 1);
      updatedItems.splice(toIndex, 0, movedItem);

      this.localItems = updatedItems;

      this.$emit("update:modelValue", updatedItems);
      this.$emit("change", updatedItems);
    },

    removeItem(index: number): void {
      if (!this.removable) {
        return;
      }

      if (index < 0 || index >= this.localItems.length) {
        return;
      }

      const removedItem = this.localItems[index];
      const updatedItems = [...this.localItems];

      updatedItems.splice(index, 1);

      this.localItems = updatedItems;

      this.$emit("update:modelValue", updatedItems);
      this.$emit("change", updatedItems);
      this.$emit("remove", removedItem, index);
    }
  }
});
</script>
